/**
 * toDraftGrid — maps validated+matched AI rows → Phase-4 grid row shape + review flags.
 *
 * AI-SPEC §6 — guardrail flags for the review UI (Wave 5).
 *
 * Critical failure mode #5 (AI-SPEC §1):
 *   "Auto-commit without review — writing parsed data straight to the live board
 *   without the AIC confirming it first."
 *
 * Contract:
 *   - Returns { rows, gridFlags } — a draft object the UI holds in React state.
 *   - Does NOT write to Firestore. The AIC confirms the draft, THEN Plan 06's
 *     onApplyRows calls saveRow / onMemberPatch / setAIC.
 *   - Each rows[i] has:
 *     a) the Phase-4 saveRow data shape keys (orRoom, startTime, endTime,
 *        surgeon, attendingId, attendingName, ahpId, ahpName, ahpShiftCode,
 *        crnaReliefId, mdReliefId, source — matching AssignmentGrid.saveRow())
 *     b) review-only row-level fields carried ALONGSIDE (not inside) that shape:
 *        callOrder, isAIC, flags
 *   - gridFlags is an array of grid-level flags (aicTag when count != 1).
 *
 * Pure domain function — no Firebase imports, no network, no AI calls.
 * Mirrors the pattern of src/domain/staff.js and src/domain/providerCodes.js.
 */

// ─── startTime helper ─────────────────────────────────────────────────────────

/**
 * Convert the schema's startTime string (3–4 military digits, no colon)
 * to the grid's HH:MM display string expected by AssignmentRow / the time dropdowns.
 *
 * Examples:
 *   "710"  → "07:10"
 *   "0730" → "07:30"
 *   "1300" → "13:00"
 *   "800"  → "08:00"
 *   null   → ""
 *
 * @param {string|null} raw  — 3–4 digit string from AssignmentRowSchema
 * @returns {string}          — "HH:MM" or "" when raw is null/blank
 */
function parseStartTime(raw) {
  if (!raw) return "";
  // Pad to 4 digits so "710" → "0710", "1300" stays "1300".
  const padded = raw.padStart(4, "0");
  return `${padded.slice(0, 2)}:${padded.slice(2, 4)}`;
}

// ─── CRNA name normalization (mirrors providerCodes.js approach) ──────────────

/**
 * Normalize a name for soft-match comparison:
 *   - lowercase
 *   - strip "Dr." prefix
 *   - strip punctuation (commas, periods, parentheses)
 *   - collapse whitespace to single space, trim
 *
 * @param {string} name
 * @returns {string}
 */
function normalizeName(name) {
  if (!name) return "";
  return name
    .toLowerCase()
    .replace(/^dr\.?\s+/i, "")
    .replace(/[,.()\[\]]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Soft-match a printed staffing member name to a CRNA/AHP roster entry by
 * normalized name comparison.
 *
 * @param {string} printedName  — name as extracted from the PDF
 * @param {Array<{id: string, name: string}>} crnas  — roster entries
 * @returns {string|null}  — matched crna.id or null when no exact match
 */
function matchCrnaByName(printedName, crnas) {
  if (!printedName || !crnas?.length) return null;
  const target = normalizeName(printedName);
  const match = crnas.find((c) => normalizeName(c.name || "") === target);
  return match?.id ?? null;
}

// ─── toDraftGrid ─────────────────────────────────────────────────────────────

/**
 * Map validated+matched AI rows to the Phase-4 assignment-grid draft shape,
 * producing per-row review flags and grid-level guardrail flags per AI-SPEC §6.
 *
 * @param {Array<object>} matchedRows
 *   Output of matchShortCodes() — each row has all AssignmentRowSchema fields
 *   PLUS { attendingDoctorId, attendingMatched }.
 *
 * @param {{ crnas: Array<{id:string, name:string}>, doctors: Array<{id:string, name:string}> }} options
 *   - crnas:   provider roster for CRNA name → id soft-matching
 *   - doctors: doctor records for resolving attendingName from matched id
 *
 * @returns {{ rows: Array<object>, gridFlags: Array<object> }}
 *   - rows: one draft row per input row, shaped for AssignmentGrid.saveRow() plus
 *           row-level review fields (callOrder, isAIC, flags)
 *   - gridFlags: grid-level flags, currently: [{ kind:"aicTag", count }] when
 *                the isAIC count is not exactly 1
 */
export function toDraftGrid(matchedRows, { crnas = [], doctors = [] }) {
  // Build a fast id → doctor lookup for attendingName resolution.
  const doctorById = new Map(doctors.map((d) => [d.id, d]));

  const rows = matchedRows.map((row) => {
    const perRowFlags = [];

    // ── Attending resolution ────────────────────────────────────────────────
    const doctor = row.attendingDoctorId ? doctorById.get(row.attendingDoctorId) : null;

    // Flag: unmatched attending short code (AI-SPEC §6 guardrail)
    if (!row.attendingMatched) {
      if (row.attendingShortCode == null) {
        // Null code → low-confidence / blank field flag
        perRowFlags.push({ field: "attendingShortCode", reason: "nullField" });
      } else {
        // Code present but no exact match → unmatched flag (failure mode #4)
        perRowFlags.push({ field: "attendingShortCode", reason: "unmatched" });
      }
    }

    // Flag: null/blank startTime → low-confidence flag (AI-SPEC §6)
    if (row.startTime == null) {
      perRowFlags.push({ field: "startTime", reason: "nullField" });
    }

    // ── Staffing: first member → ahpId / ahpName / ahpShiftCode ────────────
    const firstStaff = row.staffing?.[0] ?? null;
    let ahpId = null;
    let ahpName = "";
    let ahpShiftCode = null;

    if (!firstStaff) {
      // Empty staffing (xxxx / no AHP assigned)
      perRowFlags.push({ field: "staffing", reason: "emptyStaffing" });
    } else {
      ahpName = firstStaff.name || "";
      // ⚠️ THE RAW CODE WINS WHEN THE SOURCE HAS ONE. `timeNumber` flattens
      // "7p-7a" to 7, and 7 read back through `shiftCodeEndHour24` is 19:00 —
      // twelve hours early for the person holding the room, on a board that
      // decides who is covering it. The sheet reader threads `timeCode`; the
      // PDF/schema path has only a number, so the fallback is unchanged.
      ahpShiftCode =
        firstStaff.timeCode != null && String(firstStaff.timeCode) !== ""
          ? String(firstStaff.timeCode)
          : firstStaff.timeNumber != null
            ? String(firstStaff.timeNumber)
            : null;

      // Soft-match name against roster
      ahpId = matchCrnaByName(firstStaff.name, crnas);
      if (!ahpId && ahpName) {
        // Name present but no roster match → flag for review
        perRowFlags.push({ field: "ahpId", reason: "unmatchedCrnaName" });
      }
    }

    // ── Build the draft row ─────────────────────────────────────────────────
    // saveRow data shape (lines 166–184, AssignmentGrid.js):
    const saveRowData = {
      orRoom: row.room || "",
      startTime: parseStartTime(row.startTime),
      endTime: "",
      surgeon: "",
      attendingId: row.attendingDoctorId ?? null,
      attendingName: doctor?.name ?? "",
      ahpId,
      ahpName,
      ahpShiftCode,
      crnaReliefId: null,
      mdReliefId: null,
      source: "ai-import",
    };

    // Review-only row-level fields (NOT inside saveRow data, not persisted by saveRow).
    // Plan 06's onApplyRows maps these:
    //   callOrder → onMemberPatch("supervisors", attendingId, { callOrder })
    //   isAIC     → setAIC(attendingDoctorId)
    return {
      ...saveRowData,
      callOrder: row.callOrder ?? null,
      isAIC: Boolean(row.isAIC),
      flags: perRowFlags,
    };
  });

  // ── Grid-level flags ──────────────────────────────────────────────────────
  const gridFlags = [];
  const aicCount = rows.filter((r) => r.isAIC).length;
  if (matchedRows.length > 0 && aicCount !== 1) {
    // AI-SPEC §6 / dimension 6 — AIC tag must land on exactly one attending
    gridFlags.push({ kind: "aicTag", count: aicCount });
  }

  return { rows, gridFlags };
}
